import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CrmBadge, CrmButton, CrmCheckbox, CrmSelect, CrmSwitch } from '../components/ui';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import { listStorefrontAuthProfiles, saveStorefrontAuthProfile } from '../services/storefrontAuthProfileService';
import { listCommerceEntity } from '../services/commercePolicyService';
import '../assets/styles/Phase13Settings.scss';

const LOGIN_METHODS = [
  ['EMAIL_PASSWORD', 'Email + password'],
  ['EMAIL_OTP', 'Email OTP'],
  ['MOBILE_OTP', 'Mobile OTP'],
  ['GOOGLE', 'Google login'],
];
const ACCOUNT_FEATURES = [
  ['order_history', 'Order history'],
  ['saved_addresses', 'Saved addresses'],
  ['profile_editing', 'Profile editing'],
  ['download_invoices', 'Download invoices'],
  ['track_orders', 'Track orders'],
];

const errorText = (error) => error?.response?.data?.error || error?.message || 'Request failed.';
const normalize = (profile = {}) => ({
  ...profile,
  login_methods: Array.isArray(profile.login_methods) ? profile.login_methods : ['EMAIL_PASSWORD'],
  account_features: profile.account_features || {},
  default_customer_segment_id: profile.default_customer_segment_id || '',
});

export default function StorefrontAuthSettings() {
  const [profiles, setProfiles] = useState([]);
  const [segments, setSegments] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profileRows, segmentRows] = await Promise.all([
        listStorefrontAuthProfiles(),
        listCommerceEntity('customerSegments').catch(() => []),
      ]);
      const rows = Array.isArray(profileRows) ? profileRows : [];
      setProfiles(rows);
      setSegments(Array.isArray(segmentRows) ? segmentRows : segmentRows?.items || []);
      setSelectedId((current) => {
        if (current && rows.some((row) => String(row.storefront_id) === String(current))) return current;
        return rows[0]?.storefront_id ? String(rows[0].storefront_id) : '';
      });
    } catch (loadError) {
      setError(errorText(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const selected = profiles.find((row) => String(row.storefront_id) === String(selectedId));
    setForm(selected ? normalize(selected) : null);
  }, [profiles, selectedId]);

  const selectedProfile = useMemo(() => profiles.find((row) => String(row.storefront_id) === String(selectedId)), [profiles, selectedId]);

  const toggleMethod = (method, checked) => setForm((current) => ({
    ...current,
    login_methods: checked
      ? [...new Set([...(current.login_methods || []), method])]
      : (current.login_methods || []).filter((item) => item !== method),
  }));

  const toggleFeature = (feature, checked) => setForm((current) => ({
    ...current,
    account_features: { ...(current.account_features || {}), [feature]: checked },
  }));

  const save = async () => {
    if (!form?.storefront_id) return;
    if (!(form.login_methods || []).length && form.is_enabled) {
      const message = 'Select at least one login method when customer accounts are enabled.';
      setError(message);
      notify(message, 'warning');
      return;
    }
    if (form.require_login_before_checkout && form.allow_guest_checkout) {
      const message = 'Require login before checkout and Allow guest checkout cannot both be enabled.';
      setError(message);
      notify(message, 'warning');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const saved = await saveStorefrontAuthProfile(form.storefront_id, form);
      setProfiles((rows) => rows.map((row) => Number(row.storefront_id) === Number(saved.storefront_id) ? normalize({ ...row, ...saved }) : row));
      setForm(normalize({ ...form, ...saved }));
      notify('Customer account policy saved successfully.');
    } catch (saveError) {
      const message = errorText(saveError);
      setError(message);
      notify(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="phase13-loading">Loading storefront account settings…</div>;

  return (
    <div className="phase13-shell">
      {error ? <div className="phase13-error" role="alert">{error}</div> : null}
      <div className="phase13-auth-profile-grid">
        <div className="phase13-card">
          <div className="phase13-card__header"><div><h3>Storefronts</h3><p>Customer identity policy per storefront.</p></div><CrmButton iconOnly variant="tertiary" onClick={load} aria-label="Refresh"><RefreshIcon fontSize="small" /></CrmButton></div>
          <div className="phase13-auth-list">
            {profiles.map((profile) => (
              <button key={profile.storefront_id} type="button" className={String(profile.storefront_id) === String(selectedId) ? 'is-active' : ''} onClick={() => setSelectedId(String(profile.storefront_id))}>
                <strong>{profile.storefront_name || profile.storefront?.name || profile.storefront_code || `Storefront #${profile.storefront_id}`}</strong>
                <small>{profile.storefront_domain || profile.storefront?.domain || profile.storefront_code || ''}</small>
                <small>{profile.is_enabled ? 'Customer accounts enabled' : 'Customer accounts disabled'} · {profile.storefront_is_active === 0 || profile.storefront?.is_active === 0 ? 'Storefront inactive' : 'Storefront active'}</small>
              </button>
            ))}
            {!profiles.length ? <div className="phase13-empty">No storefront profiles are available. Run the Phase 13 migration after storefronts exist.</div> : null}
          </div>
        </div>

        <div className="phase13-card">
          {form ? (
            <>
              <div className="phase13-card__header">
                <div><h3>{selectedProfile?.storefront_name || selectedProfile?.storefront?.name || 'Customer Accounts'}</h3><p>Control signup, login, guest checkout and account capabilities independently for this storefront.</p></div>
                <CrmBadge tone={form.is_enabled ? 'success' : 'neutral'}>{form.is_enabled ? 'Enabled' : 'Disabled'}</CrmBadge>
              </div>

              <div className="phase13-form-grid phase13-form-grid--one">
                <CrmSwitch label="Enable customer accounts" helperText="Master switch for storefront account/authentication features." checked={Boolean(form.is_enabled)} onChange={(event) => setForm((p) => ({ ...p, is_enabled: event.target.checked }))} />
                <CrmSwitch label="Allow new registrations" checked={Boolean(form.allow_registration)} disabled={!form.is_enabled} onChange={(event) => setForm((p) => ({ ...p, allow_registration: event.target.checked }))} />
                <CrmSwitch label="Allow guest checkout" checked={Boolean(form.allow_guest_checkout)} disabled={!form.is_enabled || form.require_login_before_checkout} onChange={(event) => setForm((p) => ({ ...p, allow_guest_checkout: event.target.checked }))} />
                <CrmSwitch label="Require login before checkout" checked={Boolean(form.require_login_before_checkout)} disabled={!form.is_enabled} onChange={(event) => setForm((p) => ({ ...p, require_login_before_checkout: event.target.checked, allow_guest_checkout: event.target.checked ? false : p.allow_guest_checkout }))} />
              </div>

              <div className="phase13-card phase13-card--muted">
                <h4>Login methods</h4>
                <p>These capabilities are published through the storefront auth-policy API. OTP methods still require the storefront/client flow to be implemented before they can be used end-to-end.</p>
                <div className="phase13-check-grid">
                  {LOGIN_METHODS.map(([value, label]) => <CrmCheckbox key={value} label={label} checked={(form.login_methods || []).includes(value)} disabled={!form.is_enabled} onChange={(event) => toggleMethod(value, event.target.checked)} />)}
                </div>
              </div>

              <div className="phase13-form-grid">
                <CrmSwitch label="Require email verification" checked={Boolean(form.require_email_verification)} disabled={!form.is_enabled} onChange={(event) => setForm((p) => ({ ...p, require_email_verification: event.target.checked }))} />
                <CrmSwitch label="Require mobile verification" checked={Boolean(form.require_mobile_verification)} disabled={!form.is_enabled} onChange={(event) => setForm((p) => ({ ...p, require_mobile_verification: event.target.checked }))} />
                <CrmSelect label="Default customer segment" value={form.default_customer_segment_id || ''} onChange={(event) => setForm((p) => ({ ...p, default_customer_segment_id: event.target.value }))}>
                  <option value="">No automatic segment</option>
                  {segments.map((segment) => <option key={segment.id} value={segment.id}>{segment.name || segment.code || `Segment #${segment.id}`}</option>)}
                </CrmSelect>
              </div>

              <div className="phase13-card phase13-card--muted">
                <h4>Customer account features</h4>
                <div className="phase13-check-grid">
                  {ACCOUNT_FEATURES.map(([value, label]) => <CrmCheckbox key={value} label={label} checked={Boolean(form.account_features?.[value])} disabled={!form.is_enabled} onChange={(event) => toggleFeature(value, event.target.checked)} />)}
                </div>
              </div>

              <div className="phase13-card phase13-card--muted">
                <h4>Welcome communications</h4>
                <div className="phase13-check-grid">
                  <CrmCheckbox label="Send welcome email" checked={Boolean(form.send_welcome_email)} disabled={!form.is_enabled || !form.allow_registration} onChange={(event) => setForm((p) => ({ ...p, send_welcome_email: event.target.checked }))} />
                  <CrmCheckbox label="Send welcome WhatsApp" checked={Boolean(form.send_welcome_whatsapp)} disabled={!form.is_enabled || !form.allow_registration} onChange={(event) => setForm((p) => ({ ...p, send_welcome_whatsapp: event.target.checked }))} />
                </div>
                <p className="phase13-helper">These flags are policy controls. Welcome message delivery should be routed through the Communications trigger engine when the storefront registration event is wired by the storefront application.</p>
              </div>

              <div className="phase13-inline-actions"><span /><CrmButton loading={saving} startIcon={<SaveOutlinedIcon />} onClick={save}>Save customer account policy</CrmButton></div>
            </>
          ) : <div className="phase13-empty">Select a storefront to configure its customer account policy.</div>}
        </div>
      </div>
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification((current) => ({ ...current, open: false }))}
      />
    </div>
  );
}
